import { useI18n } from '@ecom/i18n';
import { Field } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import {
  fetchStoreBrandingObjectUrl,
  useUploadStoreBrandingMutation,
  type BrandingKind,
} from '../api';

import { belowRecommendedParams } from '@/lib/image-size';
import { useSession } from '@/lib/session';
import { uploadErrorMessage } from '@/lib/upload-error';

/**
 * Upload réel du logo/bannière lot — the merchant-dashboard replacement
 * for the old "URL du logo"/"Bannière" text inputs. Single file, replace
 * on re-upload (never a gallery like `ImageUploader.tsx`'s own product
 * photos) — reuses that component's exact drag/drop and error-reporting
 * pattern, simplified to one file.
 *
 * =====================================================================
 * WHICH PREVIEW SOURCE WINS — MIRRORS THE SERVER'S OWN RESOLUTION
 * =====================================================================
 *
 * `hasObjectKey` (a real upload exists) always wins and is fetched
 * through the authenticated `raw` route as a blob (`fetchStoreBrandingObjectUrl`,
 * the exact `fetchProductImageObjectUrl` pattern — a plain `<img src>`
 * cannot carry the `Authorization`/`X-Shop-Id` headers that route
 * requires). Otherwise, a LEGACY pasted URL (from before this lot) is
 * rendered directly — it is a plain external URL, no auth needed, exactly
 * how it always rendered. Neither present means no image at all yet.
 */
export function BrandingImageField({
  kind,
  label,
  hint,
  hasObjectKey,
  legacyUrl,
  disabled,
}: {
  readonly kind: BrandingKind;
  readonly label: string;
  readonly hint: string;
  readonly hasObjectKey: boolean;
  readonly legacyUrl: string | null;
  readonly disabled?: boolean | undefined;
}): ReactElement {
  const { t } = useI18n();
  const { accessToken, activeShopId } = useSession();
  const uploadMutation = useUploadStoreBrandingMutation(kind);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The image IS stored (`hasObjectKey`) but loading its preview failed — e.g. the storage link is not
  // reachable from this browser. Said out loud: an empty frame reads as "the upload did not work".
  const [previewFailed, setPreviewFailed] = useState(false);
  // The picture was accepted but is under the recommended size: stored, and the merchant is told it may look soft.
  const [sizeAdvisory, setSizeAdvisory] = useState<string | null>(null);

  useEffect(() => {
    setPreviewFailed(false);
    if (!hasObjectKey || accessToken === null || activeShopId === null) {
      setResolvedPreviewUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchStoreBrandingObjectUrl(kind, accessToken, activeShopId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setResolvedPreviewUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedPreviewUrl(null);
        setPreviewFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, hasObjectKey, accessToken, activeShopId]);

  // Tracks the latest `localPreviewUrl` for the unmount-only cleanup below
  // without making that effect re-run on every new file — the exact
  // `entriesRef` pattern `ImageUploader.tsx` already uses, for the same
  // reason: an empty dependency array reading a stale closure would never
  // revoke a preview started after the first render.
  const localPreviewUrlRef = useRef(localPreviewUrl);
  localPreviewUrlRef.current = localPreviewUrl;

  useEffect(() => {
    return () => {
      if (localPreviewUrlRef.current !== null) URL.revokeObjectURL(localPreviewUrlRef.current);
    };
  }, []);

  function handleFile(file: File | undefined): void {
    if (file === undefined) return;
    setErrorMessage(null);
    setSizeAdvisory(null);
    const previewUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(previewUrl);

    uploadMutation.mutate(file, {
      onSuccess: ({ size }) => {
        URL.revokeObjectURL(previewUrl);
        setLocalPreviewUrl(null);
        const params = belowRecommendedParams(file.name, size);
        if (params !== null) setSizeAdvisory(t('merchant.images.belowRecommended', params));
      },
      onError: (error) => {
        URL.revokeObjectURL(previewUrl);
        setLocalPreviewUrl(null);
        setErrorMessage(uploadErrorMessage(error, t));
      },
    });
  }

  const previewUrl = localPreviewUrl ?? resolvedPreviewUrl ?? legacyUrl;

  return (
    <Field
      label={label}
      optional
      hint={hint}
      {...(errorMessage !== null ? { error: errorMessage } : {})}
    >
      {() => (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            {previewUrl !== null ? (
              // Plain <img>, not next/image — merchant-dashboard is a Vite SPA, matching ProductImageGallery.tsx's own established pattern.
              <img
                src={previewUrl}
                alt=""
                className="h-16 w-16 shrink-0 rounded-md border border-line object-cover"
              />
            ) : (
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-dashed border-line text-xs text-inkFaint"
                aria-hidden="true"
              >
                {kind === 'logo' ? 'Logo' : 'Bannière'}
              </div>
            )}

            <div
              role="button"
              tabIndex={disabled === true ? -1 : 0}
              onClick={() => {
                if (disabled !== true) inputRef.current?.click();
              }}
              onKeyDown={(event) => {
                if (disabled !== true && (event.key === 'Enter' || event.key === ' ')) {
                  inputRef.current?.click();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (disabled !== true) setIsDragging(true);
              }}
              onDragLeave={() => {
                setIsDragging(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                if (disabled !== true) handleFile(event.dataTransfer.files[0]);
              }}
              className={`flex flex-1 cursor-pointer flex-col items-start justify-center gap-0.5 rounded-lg border-2 border-dashed px-4 py-3 text-left transition-colors ${
                isDragging ? 'border-accent bg-accentSoft' : 'border-line bg-ground'
              } ${disabled === true ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <p className="text-sm font-medium text-ink">
                {t('merchant.products.images.dropzone')}
              </p>
              <p className="text-xs text-inkFaint">{t('merchant.products.images.formats')}</p>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={disabled === true}
                className="hidden"
                onChange={(event) => {
                  handleFile(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </div>
          </div>
          {sizeAdvisory !== null ? (
            <p role="status" className="text-xs text-warn">
              {sizeAdvisory}
            </p>
          ) : null}
          {previewFailed && localPreviewUrl === null && resolvedPreviewUrl === null ? (
            <p role="status" className="text-xs text-warn">
              {t('merchant.store.form.previewUnavailable')}
            </p>
          ) : null}
        </div>
      )}
    </Field>
  );
}
